import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from '../../css/ProfilePage.module.css';
import { useAuth } from '../../contexts/AuthContext';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    // Redirect based on user role
    if (userRole === 'recruiter') {
      navigate('/recruiter/dashboard');
      return;
    } else if (userRole === 'universitypartner') {
      navigate('/university/dashboard');
      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await axios.get(
          'https://techservicemarket-backend.onrender.com/api/users/profile',
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        setProfile(response.data);
        setCertifications(response.data.certifications || []);
        setProjects(response.data.projects || []);
        setSkills(response.data.skills || []);
      } catch (error) {
        console.error('Error fetching profile details:', error);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    if (userRole === 'user') {
      fetchProfile();
    }
  }, [navigate, userRole]);

  // If not a regular user, don't render anything (will redirect)
  if (userRole !== 'user') {
    return <div className={styles.loading}>Redirecting...</div>;
  }

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  const deleteFile = async (type, fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `https://techservicemarket-backend.onrender.com/api/users/profile/${type}/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (type === 'certifications') {
        setCertifications(certifications.filter((cert) => cert._id !== fileId));
      } else if (type === 'projects') {
        setProjects(projects.filter((proj) => proj._id !== fileId));
      }

      setError('File deleted successfully');
    } catch (error) {
      console.error(`Error deleting ${type}:`, error.response?.data || error);
      setError(`Failed to delete ${type}.`);
    }
  };

  const downloadFile = async (type, fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/users/profile/${type}/${fileId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob', // Ensure the file is treated as binary
        }
      );

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Set the file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link); // Clean up
    } catch (error) {
      console.error(`Error downloading ${type}:`, error.response?.data || error);
      setError(`Failed to download ${type}.`);
    }
  };

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileRow}>
        <div className={styles.profileSquareCard}>
          <img src="https://wallpapers.com/images/hd/generic-person-icon-profile-ulmsmhnz0kqafcqn-2.jpg" alt="profile" />
          {profile ? (
            <>
            <h2>{profile.firstName} {profile.lastName}</h2>
            <h3>{profile.accountStatus} {profile.userRole}</h3>          
            <button
              onClick={() => navigate('/profile/edit')}
              className={styles.backButton}>
              Edit profile
            </button>
            </>
          ) : (
            <h2>Loading...</h2>
          )}
        </div>
        <div className={styles.profileRectangleCard}>
          {profile ? (
            <>
            <p><strong>First Name</strong>&emsp;&emsp;&ensp;{profile.firstName}</p>
            <p><strong>Last Name</strong>&emsp;&emsp;&ensp; {profile.lastName}</p>
            <p><strong>Email</strong>&emsp;&emsp;&emsp;&emsp;&emsp;{profile.email}</p>   
            <p><strong>Username</strong>&emsp;&emsp;&ensp; {profile.username}</p>
            <p><strong>Type</strong>&emsp;&emsp;&emsp;&emsp;&emsp; {profile.userRole}</p>
            
            </>
          ) : (
            <h2>Loading...</h2>
          )}
        </div>
      </div>
      <br></br>
      <div className={styles.profileRow}>
          <div className={styles.profileSquareCard}>
            <h2>Certifications</h2>
            <ul>
              {certifications.map((cert) => (
                <li key={cert._id}>
                  {cert.filename}{' '}
                  <br></br>
                  <button
                    onClick={() => downloadFile('certifications', cert._id, cert.name)}
                    className={styles.downloadButton}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => deleteFile('certifications', cert._id)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                  <h2></h2>
                </li>
              ))}
            </ul>            
          </div>
          <div className={styles.profileSquareCard}>
            <h2>Projects</h2>
            <ul>
              {projects.map((proj) => (
                <li key={proj._id}>
                  {proj.filename}{' '}
                  <br></br>
                  <button
                    onClick={() => downloadFile('projects', proj._id, proj.name)}
                    className={styles.downloadButton}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => deleteFile('projects', proj._id)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                  <h2></h2>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.profileSquareCard}>
            <h2>Skills</h2>
            <ul>
              {skills.map((skill, index) => (
                <li key={index}>{skill}</li>
              ))}
            </ul>
          </div>
      </div>
    </div>  
  );
};

export default ProfilePage;
